/**
 * Comment storage for excalidraw-app.
 *
 * Comments are anchored to a scene position (pin) of a specific document and
 * are stored in IndexedDB, keyed by document id. They are local-first: they
 * never leave the browser (no collab/share-link sync).
 */

import { createStore, get, set, del } from "idb-keyval";

import { documentStorage } from "../documents/documentStorage";

export type CommentReply = {
  id: string;
  author: string;
  message: string;
  createdAt: number;
};

export type CanvasComment = {
  id: string;
  /** scene coordinates the comment pin is anchored to */
  x: number;
  y: number;
  author: string;
  message: string;
  createdAt: number;
  /** timestamp the comment was resolved at; `null` while open */
  resolvedAt: number | null;
  replies: CommentReply[];
};

const commentsStore = createStore("excalidraw-comments-db", "comments-store");
const pendingCommentWrites = new Set<Promise<void>>();

const trackCommentWrite = (promise: Promise<void>) => {
  const tracked = promise.finally(() => pendingCommentWrites.delete(tracked));
  pendingCommentWrites.add(tracked);
  return tracked;
};

const commentsKey = (
  documentId: string,
  scope = documentStorage.getScope(),
) => {
  return scope === "guest"
    ? `comments:${documentId}`
    : `scope:${scope}:comments:${documentId}`;
};

export const commentStorage = {
  async loadComments(
    documentId: string,
    scope = documentStorage.getScope(),
  ): Promise<CanvasComment[]> {
    try {
      const comments = await get<CanvasComment[]>(
        commentsKey(documentId, scope),
        commentsStore,
      );
      return Array.isArray(comments) ? comments : [];
    } catch (error: any) {
      console.error(error);
      return [];
    }
  },

  saveComments(
    documentId: string,
    comments: CanvasComment[],
    scope = documentStorage.getScope(),
  ) {
    return trackCommentWrite(
      set(commentsKey(documentId, scope), comments, commentsStore),
    );
  },

  deleteComments(documentId: string, scope = documentStorage.getScope()) {
    return trackCommentWrite(
      del(commentsKey(documentId, scope), commentsStore),
    );
  },
};

export const flushCommentWrites = async () => {
  do {
    await Promise.allSettled([...pendingCommentWrites]);
  } while (pendingCommentWrites.size > 0);
};
